import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, Filter, Plus, Edit, Trash2, Eye, ToggleLeft, ToggleRight,
  Calendar, Percent, DollarSign, Loader, X, AlertCircle, CheckCircle,
  RefreshCw, Tag, Copy, TrendingUp, Users, Clock, Ticket
} from 'lucide-react';
import adminService, { 
  Coupon, CouponListResponse, CreateCouponRequest, UpdateCouponRequest,
  GetCouponsParams, CouponDetailsResponse
} from '../../services/admin/adminService';
import toast from 'react-hot-toast';

const CouponsManagement: React.FC = () => {
  const navigate = useNavigate();
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'active' | 'inactive' | 'expired' | 'all'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCoupons, setTotalCoupons] = useState(0);
  
  // Modals
  const [createModal, setCreateModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState<Coupon | null>(null);
  const [couponDetails, setCouponDetails] = useState<CouponDetailsResponse | null>(null);
  const [processing, setProcessing] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState<CreateCouponRequest>({
    coupon_code: '',
    offer_id: null,
    discount_type: 'percentage',
    discount_value: 0,
    min_purchase_amount: undefined,
    max_discount_amount: undefined,
    max_usage: undefined,
    max_usage_per_user: undefined,
    valid_from: '',
    valid_until: '',
    is_active: true,
    description: '',
  });

  useEffect(() => {
    fetchCoupons();
  }, []);

  useEffect(() => {
    fetchCoupons();
  }, [statusFilter, currentPage]);

  const fetchCoupons = async (showToast = false) => {
    try {
      if (showToast) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const params: GetCouponsParams = {
        page: currentPage,
        limit: 20,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        search: searchTerm || undefined,
      };

      const data = await adminService.getAllCoupons(params);
      setCoupons(data.coupons);
      setCurrentPage(data.pagination.current_page);
      setTotalPages(data.pagination.total_pages);
      setTotalCoupons(data.pagination.total_coupons);

      if (showToast) {
        toast.success('Coupons refreshed!');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch coupons');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setProcessing(true);
      await adminService.createCoupon(formData);
      toast.success('Coupon created successfully!');
      setCreateModal(false);
      resetForm();
      fetchCoupons();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create coupon');
    } finally {
      setProcessing(false);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCoupon) return;
    
    try {
      setProcessing(true);
      const updateData: UpdateCouponRequest = {
        coupon_id: selectedCoupon.coupon_id,
        ...formData,
      };
      await adminService.updateCoupon(updateData);
      toast.success('Coupon updated successfully!');
      setEditModal(false);
      resetForm();
      fetchCoupons();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update coupon');
    } finally {
      setProcessing(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedCoupon) return;
    
    try {
      setProcessing(true);
      await adminService.deleteCoupon(selectedCoupon.coupon_id);
      toast.success('Coupon deleted successfully!');
      setDeleteModal(false);
      setSelectedCoupon(null);
      fetchCoupons();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete coupon');
    } finally {
      setProcessing(false);
    }
  };

  const handleViewDetails = async (coupon: Coupon) => {
    try {
      setSelectedCoupon(coupon);
      const details = await adminService.getCouponById(coupon.coupon_id);
      setCouponDetails(details);
      setViewModal(true);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch coupon details');
    }
  };

  const copyCouponCode = (code: string) => {
    navigator.clipboard.writeText(code);
    toast.success('Coupon code copied to clipboard!');
  };

  const openEditModal = (coupon: Coupon) => {
    setSelectedCoupon(coupon);
    setFormData({
      coupon_code: coupon.coupon_code,
      offer_id: coupon.offer_id,
      discount_type: coupon.discount_type,
      discount_value: parseFloat(coupon.discount_value),
      min_purchase_amount: coupon.min_purchase_amount ? parseFloat(coupon.min_purchase_amount) : undefined,
      max_discount_amount: coupon.max_discount_amount ? parseFloat(coupon.max_discount_amount) : undefined,
      max_usage: coupon.max_usage || undefined,
      max_usage_per_user: coupon.max_usage_per_user || undefined,
      valid_from: coupon.valid_from.split('T')[0],
      valid_until: coupon.valid_until.split('T')[0],
      is_active: coupon.is_active === 1,
      description: coupon.description || '',
    });
    setEditModal(true);
  };

  const openDeleteModal = (coupon: Coupon) => {
    setSelectedCoupon(coupon);
    setDeleteModal(true);
  };

  const resetForm = () => {
    setFormData({
      coupon_code: '',
      offer_id: null,
      discount_type: 'percentage',
      discount_value: 0,
      min_purchase_amount: undefined,
      max_discount_amount: undefined,
      max_usage: undefined,
      max_usage_per_user: undefined,
      valid_from: '',
      valid_until: '',
      is_active: true,
      description: '',
    });
    setSelectedCoupon(null);
    setCouponDetails(null);
  };

  const getStatusBadge = (coupon: Coupon) => {
    const now = new Date();
    const validFrom = new Date(coupon.valid_from);
    const validUntil = new Date(coupon.valid_until);
    
    if (coupon.is_active === 0) {
      return <span className="px-2 py-1 bg-secondary-200 text-secondary-700 rounded-full text-xs font-semibold">Inactive</span>;
    }
    if (now < validFrom) {
      return <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">Scheduled</span>;
    }
    if (now > validUntil) {
      return <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-semibold">Expired</span>;
    }
    return <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">Active</span>;
  };

  const filteredCoupons = coupons.filter(coupon => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      coupon.coupon_code.toLowerCase().includes(search) ||
      coupon.description?.toLowerCase().includes(search) ||
      coupon.offer_title?.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader className="animate-spin text-primary-500" size={48} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900 mb-2">Coupons Management</h1>
          <p className="text-secondary-600">Create and manage coupon codes</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => fetchCoupons(true)}
            disabled={refreshing}
            className="flex items-center space-x-2 px-4 py-2 border-2 border-secondary-300 text-secondary-700 rounded-lg font-semibold hover:bg-secondary-50 transition-all disabled:opacity-50"
          >
            <RefreshCw size={20} className={refreshing ? 'animate-spin' : ''} />
            <span>Refresh</span>
          </button>
          <button
            onClick={() => navigate('/admin/coupons/create')}
            className="flex items-center space-x-2 px-4 py-2 bg-primary-500 text-white rounded-lg font-semibold hover:bg-primary-600 transition-all"
          >
            <Plus size={20} />
            <span>Create Coupon</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-soft p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-secondary-600 text-sm font-medium">Total Coupons</p>
              <p className="text-2xl font-bold text-secondary-900 mt-1">{totalCoupons}</p>
            </div>
            <Ticket className="text-primary-500" size={32} />
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-soft p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-secondary-600 text-sm font-medium">Active Coupons</p>
              <p className="text-2xl font-bold text-green-600 mt-1">
                {coupons.filter(c => c.is_active === 1 && new Date(c.valid_until) > new Date()).length}
              </p>
            </div>
            <CheckCircle className="text-green-500" size={32} />
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-soft p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-secondary-600 text-sm font-medium">Total Usage</p>
              <p className="text-2xl font-bold text-primary-600 mt-1">
                {coupons.reduce((sum, c) => sum + c.total_usage, 0)}
              </p>
            </div>
            <TrendingUp className="text-primary-500" size={32} />
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-soft p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-secondary-600 text-sm font-medium">Expired Coupons</p>
              <p className="text-2xl font-bold text-red-600 mt-1">
                {coupons.filter(c => new Date(c.valid_until) < new Date()).length}
              </p>
            </div>
            <AlertCircle className="text-red-500" size={32} />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-soft p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" size={20} />
            <input
              type="text"
              placeholder="Search coupons..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as typeof statusFilter);
              setCurrentPage(1);
            }}
            className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="expired">Expired</option>
          </select>
          <button
            onClick={() => {
              setSearchTerm('');
              setStatusFilter('all');
              setCurrentPage(1);
            }}
            className="px-4 py-2 border-2 border-secondary-300 text-secondary-700 rounded-lg font-semibold hover:bg-secondary-50 transition-all"
          >
            <Filter size={20} className="inline mr-2" />
            Reset Filters
          </button>
        </div>
      </div>

      {/* Coupons Table */}
      <div className="bg-white rounded-xl shadow-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-secondary-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase">Coupon Code</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase">Type</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase">Discount</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase">Valid Period</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase">Usage</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase">Status</th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-secondary-700 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-secondary-200">
              {filteredCoupons.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <AlertCircle className="mx-auto text-secondary-400 mb-4" size={48} />
                    <p className="text-secondary-600 font-medium">No coupons found</p>
                    <p className="text-secondary-500 text-sm mt-2">Create your first coupon to get started</p>
                  </td>
                </tr>
              ) : (
                filteredCoupons.map((coupon) => (
                  <tr key={coupon.coupon_id} className="hover:bg-secondary-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <code className="px-3 py-1 bg-primary-100 text-primary-700 rounded-lg font-mono font-semibold text-sm">
                          {coupon.coupon_code}
                        </code>
                        <button
                          onClick={() => copyCouponCode(coupon.coupon_code)}
                          className="p-1 text-secondary-500 hover:text-primary-500 transition-colors"
                          title="Copy code"
                        >
                          <Copy size={16} />
                        </button>
                      </div>
                      {coupon.description && (
                        <p className="text-xs text-secondary-500 mt-1">{coupon.description}</p>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-semibold capitalize">
                        {coupon.discount_type}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-1">
                        {coupon.discount_type === 'percentage' ? (
                          <>
                            <Percent size={16} className="text-primary-500" />
                            <span className="font-semibold text-secondary-900">{coupon.discount_value}%</span>
                          </>
                        ) : (
                          <>
                            <DollarSign size={16} className="text-primary-500" />
                            <span className="font-semibold text-secondary-900">${coupon.discount_value}</span>
                          </>
                        )}
                      </div>
                      {coupon.min_purchase_amount && (
                        <p className="text-xs text-secondary-500 mt-1">Min: ${coupon.min_purchase_amount}</p>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <div className="flex items-center space-x-1 text-secondary-600">
                          <Calendar size={14} />
                          <span>{new Date(coupon.valid_from).toLocaleDateString()}</span>
                        </div>
                        <div className="flex items-center space-x-1 text-secondary-600 mt-1">
                          <Clock size={14} />
                          <span>{new Date(coupon.valid_until).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <p className="font-semibold text-secondary-900">{coupon.total_usage}</p>
                        {coupon.max_usage && (
                          <p className="text-xs text-secondary-500">of {coupon.max_usage}</p>
                        )}
                        {coupon.max_usage_per_user && (
                          <p className="text-xs text-secondary-500">({coupon.max_usage_per_user} per user)</p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(coupon)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => navigate(`/admin/coupons/${coupon.coupon_id}`)}
                          className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-all"
                          title="View Details"
                        >
                          <Eye size={18} />
                        </button>
                        <button
                          onClick={() => navigate(`/admin/coupons/${coupon.coupon_id}/edit`)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                          title="Edit"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => openDeleteModal(coupon)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-all"
                          title="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-secondary-200 flex items-center justify-between">
            <p className="text-sm text-secondary-600">
              Showing page {currentPage} of {totalPages} ({totalCoupons} total coupons)
            </p>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 border-2 border-secondary-300 text-secondary-700 rounded-lg font-semibold hover:bg-secondary-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 border-2 border-secondary-300 text-secondary-700 rounded-lg font-semibold hover:bg-secondary-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {createModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-secondary-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-secondary-900">Create New Coupon</h2>
              <button
                onClick={() => {
                  setCreateModal(false);
                  resetForm();
                }}
                className="text-secondary-500 hover:text-secondary-700"
              >
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">
                    Coupon Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.coupon_code}
                    onChange={(e) => setFormData({...formData, coupon_code: e.target.value.toUpperCase()})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none font-mono"
                    placeholder="SAVE20"
                    maxLength={20}
                  />
                  <p className="text-xs text-secondary-500 mt-1">3-20 characters, uppercase</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">
                    Discount Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    required
                    value={formData.discount_type}
                    onChange={(e) => setFormData({...formData, discount_type: e.target.value as 'percentage' | 'fixed'})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                  >
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed Amount</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">
                    Discount Value <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={formData.discount_value}
                    onChange={(e) => setFormData({...formData, discount_value: parseFloat(e.target.value) || 0})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                    placeholder="Enter discount value"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">Min Purchase Amount</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.min_purchase_amount || ''}
                    onChange={(e) => setFormData({...formData, min_purchase_amount: e.target.value ? parseFloat(e.target.value) : undefined})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                    placeholder="Enter minimum purchase amount"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">Max Discount Amount</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.max_discount_amount || ''}
                    onChange={(e) => setFormData({...formData, max_discount_amount: e.target.value ? parseFloat(e.target.value) : undefined})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                    placeholder="Enter maximum discount amount"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">Max Usage</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.max_usage || ''}
                    onChange={(e) => setFormData({...formData, max_usage: e.target.value ? parseInt(e.target.value) : undefined})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                    placeholder="Enter max usage limit"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">Max Usage Per User</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.max_usage_per_user || ''}
                    onChange={(e) => setFormData({...formData, max_usage_per_user: e.target.value ? parseInt(e.target.value) : undefined})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                    placeholder="Enter max usage per user"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">
                    Valid From <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.valid_from}
                    onChange={(e) => setFormData({...formData, valid_from: e.target.value})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">
                    Valid Until <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.valid_until}
                    onChange={(e) => setFormData({...formData, valid_until: e.target.value})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                    rows={3}
                    placeholder="Enter coupon description"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                      className="w-4 h-4 text-primary-500 border-secondary-300 rounded focus:ring-primary-500"
                    />
                    <span className="text-sm font-semibold text-secondary-700">Active</span>
                  </label>
                </div>
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t border-secondary-200">
                <button
                  type="button"
                  onClick={() => {
                    setCreateModal(false);
                    resetForm();
                  }}
                  className="px-6 py-2 border-2 border-secondary-300 text-secondary-700 rounded-lg font-semibold hover:bg-secondary-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={processing}
                  className="px-6 py-2 bg-primary-500 text-white rounded-lg font-semibold hover:bg-primary-600 transition-all disabled:opacity-50"
                >
                  {processing ? 'Creating...' : 'Create Coupon'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModal && selectedCoupon && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-secondary-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-secondary-900">Edit Coupon</h2>
              <button
                onClick={() => {
                  setEditModal(false);
                  resetForm();
                }}
                className="text-secondary-500 hover:text-secondary-700"
              >
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleUpdate} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">
                    Coupon Code
                  </label>
                  <input
                    type="text"
                    value={formData.coupon_code}
                    disabled
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg bg-secondary-50 font-mono cursor-not-allowed"
                  />
                  <p className="text-xs text-secondary-500 mt-1">Coupon code cannot be changed</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">
                    Discount Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    required
                    value={formData.discount_type}
                    onChange={(e) => setFormData({...formData, discount_type: e.target.value as 'percentage' | 'fixed'})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                  >
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed Amount</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">
                    Discount Value <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={formData.discount_value}
                    onChange={(e) => setFormData({...formData, discount_value: parseFloat(e.target.value) || 0})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">Min Purchase Amount</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.min_purchase_amount || ''}
                    onChange={(e) => setFormData({...formData, min_purchase_amount: e.target.value ? parseFloat(e.target.value) : undefined})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">Max Discount Amount</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.max_discount_amount || ''}
                    onChange={(e) => setFormData({...formData, max_discount_amount: e.target.value ? parseFloat(e.target.value) : undefined})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">Max Usage</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.max_usage || ''}
                    onChange={(e) => setFormData({...formData, max_usage: e.target.value ? parseInt(e.target.value) : undefined})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">Max Usage Per User</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.max_usage_per_user || ''}
                    onChange={(e) => setFormData({...formData, max_usage_per_user: e.target.value ? parseInt(e.target.value) : undefined})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">
                    Valid From <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.valid_from}
                    onChange={(e) => setFormData({...formData, valid_from: e.target.value})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">
                    Valid Until <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.valid_until}
                    onChange={(e) => setFormData({...formData, valid_until: e.target.value})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                    rows={3}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                      className="w-4 h-4 text-primary-500 border-secondary-300 rounded focus:ring-primary-500"
                    />
                    <span className="text-sm font-semibold text-secondary-700">Active</span>
                  </label>
                </div>
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t border-secondary-200">
                <button
                  type="button"
                  onClick={() => {
                    setEditModal(false);
                    resetForm();
                  }}
                  className="px-6 py-2 border-2 border-secondary-300 text-secondary-700 rounded-lg font-semibold hover:bg-secondary-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={processing}
                  className="px-6 py-2 bg-primary-500 text-white rounded-lg font-semibold hover:bg-primary-600 transition-all disabled:opacity-50"
                >
                  {processing ? 'Updating...' : 'Update Coupon'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {deleteModal && selectedCoupon && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertCircle className="text-red-600" size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-secondary-900">Delete Coupon</h2>
                  <p className="text-sm text-secondary-600">This action cannot be undone</p>
                </div>
              </div>
              <p className="text-secondary-700 mb-6">
                Are you sure you want to delete the coupon <strong>"{selectedCoupon.coupon_code}"</strong>?
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setDeleteModal(false);
                    setSelectedCoupon(null);
                  }}
                  className="px-6 py-2 border-2 border-secondary-300 text-secondary-700 rounded-lg font-semibold hover:bg-secondary-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={processing}
                  className="px-6 py-2 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600 transition-all disabled:opacity-50"
                >
                  {processing ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {viewModal && selectedCoupon && couponDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-secondary-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-secondary-900">Coupon Details</h2>
              <button
                onClick={() => {
                  setViewModal(false);
                  setSelectedCoupon(null);
                  setCouponDetails(null);
                }}
                className="text-secondary-500 hover:text-secondary-700"
              >
                <X size={24} />
              </button>
            </div>
            <div className="p-6 space-y-6">
              <div className="bg-primary-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-secondary-600 mb-1">Coupon Code</p>
                    <code className="text-2xl font-mono font-bold text-primary-700">{selectedCoupon.coupon_code}</code>
                  </div>
                  <button
                    onClick={() => copyCouponCode(selectedCoupon.coupon_code)}
                    className="p-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-all"
                    title="Copy code"
                  >
                    <Copy size={20} />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-secondary-600 mb-1">Status</p>
                  {getStatusBadge(selectedCoupon)}
                </div>
                <div>
                  <p className="text-sm text-secondary-600 mb-1">Type</p>
                  <p className="font-semibold text-secondary-900 capitalize">{selectedCoupon.discount_type}</p>
                </div>
                <div>
                  <p className="text-sm text-secondary-600 mb-1">Discount</p>
                  <p className="font-semibold text-secondary-900">
                    {selectedCoupon.discount_type === 'percentage' 
                      ? `${selectedCoupon.discount_value}%`
                      : `$${selectedCoupon.discount_value}`}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-secondary-600 mb-1">Usage</p>
                  <p className="font-semibold text-secondary-900">
                    {selectedCoupon.total_usage} {selectedCoupon.max_usage ? `of ${selectedCoupon.max_usage}` : ''}
                  </p>
                </div>
                {selectedCoupon.description && (
                  <div className="col-span-2">
                    <p className="text-sm text-secondary-600 mb-1">Description</p>
                    <p className="text-secondary-900">{selectedCoupon.description}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-secondary-600 mb-1">Valid From</p>
                  <p className="font-semibold text-secondary-900">{new Date(selectedCoupon.valid_from).toLocaleDateString()}</p>
                </div>
                <div>
                  <p className="text-sm text-secondary-600 mb-1">Valid Until</p>
                  <p className="font-semibold text-secondary-900">{new Date(selectedCoupon.valid_until).toLocaleDateString()}</p>
                </div>
                {selectedCoupon.min_purchase_amount && (
                  <div>
                    <p className="text-sm text-secondary-600 mb-1">Min Purchase</p>
                    <p className="font-semibold text-secondary-900">${selectedCoupon.min_purchase_amount}</p>
                  </div>
                )}
                {selectedCoupon.max_discount_amount && (
                  <div>
                    <p className="text-sm text-secondary-600 mb-1">Max Discount</p>
                    <p className="font-semibold text-secondary-900">${selectedCoupon.max_discount_amount}</p>
                  </div>
                )}
                {selectedCoupon.max_usage_per_user && (
                  <div>
                    <p className="text-sm text-secondary-600 mb-1">Max Per User</p>
                    <p className="font-semibold text-secondary-900">{selectedCoupon.max_usage_per_user}</p>
                  </div>
                )}
              </div>
              {couponDetails.usage_stats && couponDetails.usage_stats.length > 0 && (
                <div className="border-t border-secondary-200 pt-6">
                  <h3 className="text-lg font-bold text-secondary-900 mb-4">Usage Statistics</h3>
                  <div className="space-y-2">
                    {couponDetails.usage_stats.map((stat, index) => (
                      <div key={index} className="bg-secondary-50 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-semibold text-secondary-900">{stat.total_usage} uses</p>
                            <p className="text-sm text-secondary-600">{stat.unique_users} unique users</p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-primary-600">${stat.total_discount}</p>
                            <p className="text-xs text-secondary-500">Total discount</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CouponsManagement;

